(ns quuppa.peweb.peinfo-events
  (:require [clojure.set :as set]
            [clojure.string :as str]
            [ajax.core :as ajax]
            [re-frame.core :as rf]
            [quuppa.frontend-utils :as utils]
            [quuppa.qpe.pe.universalformat.universal-target :as ut]
            [quuppa.peweb.peinfo-utils :as pe-utils]
            quuppa.peweb.peinfo-subs
            quuppa.peweb.mapview-events))

(def default-qpe
  {:loading {:server-info true
             :controller-state true
             :system-status true}})

(rf/reg-event-db
  :qpe/assoc-in
  (fn [db [_ qpe-identity kv v]]
    (update-in db [:qpes qpe-identity] assoc-in kv v)))

(rf/reg-event-db
  :qpe/dissoc-in
  (fn [db [_ qpe-identity kvs]]
    (assert (vector? kvs) "dissoc-in must use vector path")
    (let [[path last-k] ((juxt pop peek) kvs)]
      (update-in db (into [:qpes qpe-identity] path) dissoc last-k))))

;; Every event handler and sub handler fn is named for Chrome profiler.

(rf/reg-event-db
  :qpe/server-info
  (fn [db [_ qpe-identity server-info]]
    (update-in db [:qpes qpe-identity]
               (fn [x]
                 (-> x
                     (update :loading assoc :server-info false)
                     (assoc :server-info server-info))))))

(rf/reg-event-db
  :qpe/qpe-metadata
  (fn [db [_ qpe-identity metadata]]
    (assoc-in db [:qpes qpe-identity :server-info :qpe-metadata] metadata)))

(rf/reg-event-fx
  :qpe/controller-state
  (fn event-qpe-controller-state
    [{db :db} [_ qpe-identity controller-state]]
    ; also update message keys to controller-ui, we keep display status of messages there
    ; NOTE there's a problem here if we wanted to force a message shown with the same id but updated message
    ; to be shown

    ; NOTE as a side-effect request project info if controller is in simulator mode and we haven't gotten it
    ; (because qsp uploaded it to qpe, we don't get it ad current-project/end-changing)
    ;(println id "simulator mode pe running" (and (get-in db [:qpe :controller-state :simulator-mode?])
    ;                                             (get-in db [:qpe :controller-state :pe-running?]))
    ;         "project-cs-maps-nil?" (nil? (get-in db [:current-project :cs-maps])))

    (let [;; If existing-projects list doesn't include this project,
          ;; try load load list again.
          ;; FIXME: If list still doesn't return the project, this is triggered again every 5 sec?
          ;; TODO: Prevent multiple requests?
          ;; For some reason license-key is "BinaryLogMode" if we are in binary log mode. Real project-uuid would
          ;; be available on :current-project.
          site-plan-key (:license-key controller-state)
          site-plan-key (if (= "BinaryLogMode" site-plan-key)
                         nil
                         site-plan-key)
          new-site-plan? (when site-plan-key
                           (not (get-in db [:qpes qpe-identity :existing-projects site-plan-key])))
          license-info (get-in db [:qpes qpe-identity :current-project-license])

          running? (and (:simulator-mode? controller-state)
                        (:pe-running? controller-state)
                        (nil? (get-in db [:qpes qpe-identity :current-project :cs-maps])))]

      {:db (-> db
               (assoc-in [:qpes qpe-identity :loading :controller-state] false)
               (update-in [:qpes qpe-identity :controller-ui :messages] #(merge (:messages controller-state) %))
               (assoc-in [:qpes qpe-identity :controller-state] controller-state)
               ; nullify project if we were in simulator mode and pe is running but new state is stopped
               ; NOTE perhaps we should create these synthetic events on the server side instead
               (cond->
                 (and (get-in db [:qpes qpe-identity :controller-state :simulator-mode?])
                      (get-in db [:qpes qpe-identity :controller-state :pe-running?])
                      (not (:pe-running? controller-state)))
                 (assoc-in [:qpes qpe-identity :current-project] nil)))
       :fx [(when running?
              [:qpe-dispatch {:qpe-identity qpe-identity
                              :event [:current-project/req-info]}])
            (when running?
              [:qpe-dispatch {:qpe-identity qpe-identity
                              :event [:tags/req-info-all {:active-cs nil}]}])
            ; Especially for simulations where all tags are runtime only,
            ; we need to fetch the initial tags info once the tags have been created
            (when running?
              [:dispatch-later {:ms       10000
                                :dispatch [:init/req-tags {:identity qpe-identity}]}])

            (when-not license-info
              ;; if QPE comes 'online' after a while from when UI has send initial req-license,
              ;; license info is missing - there could possible be a better place to req that too when
              ;; "QPE connects", like maybe even in `:qsm/report-qpe-version`?
              [:qpe-dispatch {:qpe-identity qpe-identity
                              :event [:current-project/req-license]}])

            ;; Wait until files are synced, as the list doesn't include the project
            ;; else.
            (when (and (not (:file-sync-disabled? controller-state))
                       new-site-plan?
                       (not (:file-sync-running? controller-state)))
              [:qpe-dispatch {:qpe-identity qpe-identity
                              :event [:qpe/req-existing-projects]}])]})))

(rf/reg-event-db
  :qpe/existing-projects
  (fn event-qpe-existing-projects
    [db [_ qpe-identity existing-projects]]
    (assoc-in db [:qpes qpe-identity :existing-projects] existing-projects)))

(rf/reg-event-fx
  :qpe/remove-existing-project
  (fn event-qpe-remove-existing-project
    [_ [_ qpe-identity project-id]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:qpe/remove-project project-id]}]]}))

(rf/reg-event-db
  :controller-ui/close-message
  (fn event-controller-ui-close-message
    [db [_ qpe-identity message-id]]
    (assoc-in db [:qpes qpe-identity :controller-ui :messages message-id :closed?] true)))

(rf/reg-event-fx
  :qpe/force-file-sync
  (fn event-qpe-force-client-sync
    [{db :db} [_ qpe-identity]]
    {:db (assoc-in db [:qpes qpe-identity :controller-ui :state-flags :req-file-sync] true)
     :fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/req-file-sync]}]]}))

(rf/reg-event-fx
  :qpe/select-existing-project
  (fn event-qpe-select-existing-project
    [{db :db} [_ qpe-identity project-info-row]]
    (let [new-project (get-in db [:qpes qpe-identity :controller-ui :new-project])]
      {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                            :event        [:qpe/req-select-submitted-project (merge project-info-row
                                                                                    (select-keys new-project [:binary-log-mode]))]
                            :on-success   :ignore
                            :on-error     [:qpe/project-change-failed qpe-identity]}]]})))

(rf/reg-event-fx
  :qpe/upload-project
  (fn event-qpe-select-existing-project
    [{db :db} [_ qpe-identity]]
    ;; File contents are encoded in Base64 to send them over WS.
    ;; Normally I'd just use HTTP multipart request for this, but
    ;; to ensure this works for QSM, it is easiest to send this also over
    ;; WS.
    (let [files (get-in db [:qpes qpe-identity :controller-ui :new-project :files])]
      (.then (js/Promise.all (map utils/file->base64 files))
             (fn [data]
               (rf/dispatch [:qpe/upload-project-2 qpe-identity (js->clj data)]))))
    nil))

(rf/reg-event-fx
  :qpe/upload-project-2
  (fn event-qpe-select-existing-project
    [{db :db} [_ qpe-identity files]]
    (let [new-project (get-in db [:qpes qpe-identity :controller-ui :new-project])]
      {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                            :event        [:qpe/req-upload-new-project (-> new-project
                                                                           (select-keys [:binary-log-mode])
                                                                           (assoc :files files))]
                            :on-success   :ignore
                            :on-error     [:qpe/project-change-failed qpe-identity]}]]})))

(rf/reg-event-fx
  :qpe/controller-set-simulator-mode
  (fn event-qpe-controller-set-simulator-mode
    [{db :db} [_ qpe-identity value]]
    {:db (assoc-in db [:qpes qpe-identity :controller-ui :state-flags :req-simulator-mode] value)
     :fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:peinfo/req-simulator-mode value]
                          :on-success   :ignore
                          :on-error     [:qpe/project-change-failed qpe-identity]}]]}))

(rf/reg-event-fx
  :qpe/select-project
  (fn event-qpe-controller-set-submitted-project
    [{db :db} [_ qpe-identity]]
    (let [project  (get-in db [:qpes qpe-identity :controller-ui :new-project])
          k (:license-key project)]
      {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                            :event        [:qpe/req-select-submitted-project
                                           {:key             k
                                            ; :path for submitted project key is the same as key, perhaps we should fill this in only on server?
                                            :path            k
                                            :password        (:license-password project)
                                            :type            :online
                                            :binary-log-mode (:binary-log-mode project)}]
                            :on-success   :ignore
                            :on-error     [:qpe/project-change-failed qpe-identity]}]]})))

(rf/reg-event-fx
  :qpe/project-change-failed
  (fn [_ [_ _qpe-identity error]]
    (js/console.warn "Project change error" (clj->js error))
    {:fx [[:dispatch [:notifications/notify error]]]}))

(rf/reg-event-fx
  :qpe/start-changing-project
  (fn event-qpe-start-changing-project
    [{db :db} [_ qpe-identity]]
    {:db (-> db
             ; in case wasn't already set
             (assoc-in [:qpes qpe-identity :controller-ui :state-flags :req-change-project] true)
             (assoc-in [:qpes qpe-identity :controller-ui :state-flags :req-run] true)
             (assoc-in [:qpes qpe-identity :current-project] nil)
             (assoc-in [:qpes qpe-identity :tags] {:location {}
                                                   :info {}}))}))

(rf/reg-event-fx
  :qpe/end-changing-project
  (fn event-qpe-end-changing-project
    [{db :db} [_ qpe-identity]]
    {:db (-> db
             (assoc-in [:qpes qpe-identity :controller-ui :state-flags :req-change-project] false)
             (assoc-in [:qpes qpe-identity :controller-ui :state-flags :req-run] false)
             (update-in [:qpes qpe-identity :ui] dissoc :sidebar)
             (assoc-in [:qpes qpe-identity :project-ui :license-dialog-open?] false))
     :fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:current-project/req-info]}]
          [:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:current-project/req-license]}]
          [:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:current-project/req-state]}]
          [:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:qpe/req-controller-state]}]]}))

(rf/reg-event-fx
  :qpe/run
  (fn event-qpe-run
    [{db :db} [_ qpe-identity value]]
    {:db (assoc-in db [:qpes qpe-identity :controller-ui :state-flags :req-run] true)
     :fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:qpe/req-run value]}]]}))


(rf/reg-event-fx
  :current-project/select-active-cs
  (fn event-current-project-select-active-cs
    [_ [_ qpe-identity cs-id]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:connection/req-set-preferences {:active-cs cs-id}]}]
          [:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:tags/req-info-all {:active-cs cs-id}]}]]}))

(rf/reg-event-db
  :qpe/running?
  (fn event-qpe-running?
    [db [_ qpe-identity partial-state]]
    (-> db
        (assoc-in [:qpes qpe-identity :controller-ui :state-flags :req-run] false)
        (update-in [:qpes qpe-identity :controller-state] merge partial-state))))

(rf/reg-event-db
  :qpe/logfile-list
  (fn [db [_ qpe-identity log-file-list]]
    (-> db
        (assoc-in [:qpes qpe-identity :logfile-list] log-file-list)
        (update-in [:qpes qpe-identity] dissoc :logfile-list-loading))))

(rf/reg-event-db
  :qpe/logfile-reset
  (fn [db [_ qpe-identity]]
    (update-in db [:qpes (or qpe-identity (:qpe-identity db))] dissoc :logfile-content)))

(rf/reg-event-db
  :qpe/show-current-log
  (fn [db [_ qpe-identity]]
    (assoc-in db [:qpes  (or qpe-identity (:qpe-identity db)) :logfile-content] :current-log)))

(rf/reg-event-db
  :log/append-log
  (fn [db [_ qpe-identity log-string]]
    ;; TODO: does this work? indices seem weird when looking from debug view...
    (let [max-log-length 1000
          current-rolling-log (get-in db [:qpes qpe-identity :current-rolling-log-file])
          current-rolling-log (if current-rolling-log (conj current-rolling-log log-string) [log-string])
          current-rolling-log (if (> max-log-length (count current-rolling-log))
                                current-rolling-log (vec (take-last max-log-length current-rolling-log)))]
      (assoc-in db [:qpes qpe-identity :current-rolling-log-file] current-rolling-log))))

;; Tags info is separated to its own key under qpe map, so this doesn't reset that.
(rf/reg-event-db
  :current-project/info
  (fn event-current-project-info
    [db [_ qpe-identity project-info]]
    (-> db
        (assoc-in [:qpes qpe-identity :current-project] project-info)
        (assoc-in [:qpes qpe-identity :controller-ui :state-flags :req-file-sync] false))))

(rf/reg-event-db
  :current-project/license
  (fn event-current-project-license
    [db [_ qpe-identity license]]
    (assoc-in db [:qpes qpe-identity :current-project-license] license)))

(rf/reg-event-db
  :current-project/info-partial
  (fn event-current-project-info-partial
    [db [_ qpe-identity partial-info]]
    (update-in db [:qpes qpe-identity :current-project] merge partial-info)))

(rf/reg-event-db
  :current-project/state
  (fn event-current-project-state
    [db [_ qpe-identity project-state]]
    (let [pending?            (get-in db [:qpes qpe-identity :ui :fw-update-pending?])
          pending-locator-ids (get-in db [:qpes qpe-identity :ui :fw-update-pending-locator-ids])
          locators            (merge (:runtime-locators project-state) (:locators project-state))
          confirmed?          (and pending? (seq pending-locator-ids)
                                   (some (fn [[locator-id locator]]
                                           (and (contains? pending-locator-ids locator-id)
                                                (= :FWUpgrade (or (:connection-status locator)
                                                                  (:command-status locator)))))
                                         locators))
          new-db (assoc-in db [:qpes qpe-identity :project-state] project-state)]
      (if confirmed?
        ;; Real status now confirms the upgrade started for at least one
        ;; targeted locator - :any-locator-upgrading? will keep the button
        ;; disabled/pending from here on, driven by live status, so the
        ;; bridging flag can be safely cleared with no gap.
        (-> new-db
            (assoc-in [:qpes qpe-identity :ui :fw-update-pending?] false)
            (update-in [:qpes qpe-identity :ui] dissoc :fw-update-pending-locator-ids))
        new-db))))

(defn- sanitize-hw-type-prefs
  "Remove any stale hw-type column preferences from user-preferences."
  [prefs]
  (let [hw-type-cols #{:hw-type}]
    (-> prefs
        (update :locator-columns
                (fn [cols]
                  (when cols
                    (vec (remove hw-type-cols cols)))))
        (update-in [:locator-filters :hw-type] #(when % (disj % :hw-type))))))

(rf/reg-event-fx
  :init/state-all
  (fn event-init-state-all
    [{db :db} [_ props]]
    (let [qpe-identity (:identity props)
          site-id (:site-id props)
          user-preferences (utils/get-item! "user-preferences")]
      (when goog/DEBUG
        (js/console.debug ":init/state-all for qpe" qpe-identity ", site:" site-id))
      ;; We previously always reset the qpe value to default value.
      ;; In QPE-next at least we might get some info into :qpes before this is triggred,
      ;; so looks like we don't want to reset everything here.
      ;; In QSM this is triggered when navigation to QPEs page.
      ;; Not sure if not clearing data will cause problems there.
      {:db (if qpe-identity
             ;; FIXME: Can't do this if we only have site-id
             (-> db
                 (assoc :user-preferences user-preferences)
                 (update-in [:qpes qpe-identity] merge (assoc default-qpe :server-info props)))
             (assoc db :user-preferences user-preferences))
       :fx (->> [[:qpe/req-server-info]
                 [:qpe/req-controller-state]
                 [:qpe/req-existing-projects]
                 [:current-project/req-info]
                 [:current-project/req-license]
                 [:current-project/req-state]]
                (mapv #(vector :qpe-dispatch {:event % :qpe-identity qpe-identity :site-id site-id})))})))

(rf/reg-event-fx
  :init/req-tags
  (fn event-init-req-tags
    [_ [_ props]]
    {:fx [[:qpe-dispatch {:qpe-identity (:identity props)
                          :site-id (:site-id props)
                          :event [:tags/req-info-all {:active-cs nil}]}]]}))

(rf/reg-event-db
  :controller-ui/expand-license-details
  (fn event-controller-ui-expand-license-details
    [db [_ qpe-identity value]]
    (assoc-in db [:qpes qpe-identity :controller-ui :state-flags :license-details-expanded?] value)))

(rf/reg-event-db
  :controller-ui/configuration-data
  (fn event-controller-ui-configuration-data
    [db [_ qpe-identity ui-configuration-data]]
    (update-in db [:qpes qpe-identity :controller-ui] merge ui-configuration-data)))

(rf/reg-event-db
  :project-ui/open-license-dialog
  (fn event-project-ui-open-license-dialog
    [db [_ qpe-identity value]]
    (-> db
        (update-in [:qpes qpe-identity]
                   (fn [v]
                     (-> v
                         (assoc-in [:project-ui :license-dialog-open?] value)
                         ;; Reset set key / upload project form when opening or closing dialog.
                         (update :controller-ui dissoc :new-project)))))))

(rf/reg-event-db
  :project-ui/select-project-select-dialog-tab
  (fn event-project-ui-select-project-select-dialog-tag
    [db [_ qpe-identity tab-id]]
    (assoc-in db [:qpes qpe-identity :project-ui :selected-project-select-dialog-tab] tab-id)))

(rf/reg-event-db
  :qpe/set-desired-logging-output-config
  (fn set-desired-logging-output-config
    [db [_ qpe-identity field value]]
    (assoc-in db [:qpes qpe-identity :desired-logging-output-config field] value)))

(rf/reg-event-db
  :qpe/success-file-logging-api-call
  (fn success-create-file-logging
    [db [_ qpe-identity result]]
    (-> db
        (update-in [:qpes qpe-identity :desired-logging-output-config] dissoc :xhrio-error)
        (assoc-in [:qpes qpe-identity :desired-logging-output-config :xhrio-success] result))))

(rf/reg-event-db
  :qpe/failure-file-logging-api-call
  (fn failure-create-file-logging
    [db [_ qpe-identity result]]
    (-> db
        (update-in [:qpes qpe-identity :desired-logging-output-config] dissoc :xhrio-success)
        (assoc-in [:qpes qpe-identity :desired-logging-output-config :xhrio-error] result))))

(rf/reg-event-fx
  :qpe/create-file-logging
  (fn create-file-logging
    [_ [_ qpe-identity {log-type :type,
                        :keys [format-id extrapolation folder]}]]
    {:fx [[:http-xhrio {:method          :post
                        :uri             (str "/qpe/" qpe-identity "/v2/logging")
                        :url-params      {:mode          "create"
                                          :id            format-id
                                          :type          log-type
                                          :extrapolation extrapolation
                                          :folder        folder}
                        :on-success      [:qpe/success-file-logging-api-call qpe-identity]
                        :on-failure      [:qpe/failure-file-logging-api-call qpe-identity]
                        :format          (ajax/json-request-format)
                        :response-format (ajax/json-response-format {:keywords? true})}]]}))

(rf/reg-event-fx
  :qpe/stop-file-output
  (fn stop-file-output
    [_ [_ qpe-identity target]]
    {:fx [[:http-xhrio {:method          :post
                        :uri             (str "/qpe/" qpe-identity "/v2/logging")
                        :url-params      {:mode  "delete"
                                          :id    target
                                          ;; FIXME: Should the API be changed?
                                          :value "anything but can not skip"}
                        :on-success      [:qpe/success-file-logging-api-call qpe-identity]
                        :on-failure      [:qpe/failure-file-logging-api-call qpe-identity]
                        :format          (ajax/json-request-format)
                        :response-format (ajax/json-response-format {:keywords? true})}]]}))

(rf/reg-event-fx
  :qpe/control-binary-logging
  (fn event-qpe-toggle-binary-logging
    [_ [_ qpe-identity params]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/binary-logging params]
                          :on-success   [:qpe/close-sidebar qpe-identity]
                          :on-error     [:qpe/sidebar-error qpe-identity]}]
          [:dispatch [:qpe/sidebar-pending qpe-identity true]]]}))

(rf/reg-event-fx
  :qpe/control-data-player
  (fn [_ [_ qpe-identity params]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/control-data-player params]
                          :on-success   [:qpe/close-sidebar qpe-identity]
                          :on-error     [:qpe/sidebar-error qpe-identity]}]
          [:dispatch [:qpe/sidebar-pending qpe-identity true]]]}))

(rf/reg-event-fx
  :qpe/create-universal-target-success
  (fn [_ [_ qpe-identity {:keys [data files]}]]
    {:fx [(when (and (some #{(:MQTTScheme data)} ["ssl" "wss"]) (seq files))
            [:dispatch [:qsm/save-uploaded-resource qpe-identity {:files         files
                                                                  :resource-type "zipFile"
                                                                  :target-id (:id data)}]])
          [:dispatch [:qpe/close-sidebar qpe-identity]]]}))

(rf/reg-event-fx
  :qpe/create-universal-target
  (fn [_ [_ qpe-identity params]]
    (let [{:keys [MQTTScheme WebsocketScheme]} params
          modified-scheme (if (= MQTTScheme "ssl/tls")
                            "ssl"
                            MQTTScheme)
          data (if MQTTScheme
                 (cond-> (dissoc params :MQTTScheme)
                         modified-scheme (update :MQTTAddress
                                          (if (some #{modified-scheme} ["ws" "wss"])
                                            (fn [addr]
                                              (pe-utils/update-address addr modified-scheme "/mqtt"))
                                            (fn [addr]
                                              (pe-utils/update-address addr modified-scheme "")))))
                 (cond-> (dissoc params :WebsocketScheme)
                         WebsocketScheme (update :WebsocketAddress
                                        (fn [addr]
                                          (pe-utils/update-address addr WebsocketScheme "/")))))]
      {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                            :event        [:qpe/create-universal-target data]
                            :on-success   [:qpe/create-universal-target-success qpe-identity]
                            :on-error     [:qpe/sidebar-error qpe-identity]}]
            [:dispatch [:qpe/sidebar-pending qpe-identity true]]]})))

(rf/reg-event-fx :qpe/universal-target-form-change
  (fn [_ [_ qpe-identity field value]]
    {:fx [[:dispatch [:qpe/assoc-in qpe-identity [:ui :sidebar :ut field] value]]
          (when (and (= :triggerMode field)
                     (not= :interval value))
            [:dispatch [:qpe/dissoc-in qpe-identity [:ui :sidebar :ut :interval]]])]}))

(rf/reg-event-db :remove-upload-files-error
                 (fn [db [_ qpe-identity]]
                   (update db [:qpes qpe-identity :ui :sidebar :ut] dissoc :errors)))

(rf/reg-event-fx :qpe/universal-target-form-errors
                 (fn [_ [_ qpe-identity value]]
                   {:fx [[:dispatch [:qpe/assoc-in qpe-identity [:ui :sidebar :ut :errors] value]]]}))

(rf/reg-event-fx :qpe/clear-universal-target-form-errors
                 (fn [_ [_ qpe-identity]]
                   {:fx [[:dispatch [:qpe/dissoc-in qpe-identity [:ui :sidebar :ut :errors]]]]}))

(rf/reg-event-fx
  :qpe/control-universal-target-success
  (fn [_ [_ mode site-plan-key qpe-identity {:keys [data files]}]]
    {:fx [(when (and (= mode "delete")
                     (some #{(:MQTTScheme data)} ["ssl" "wss"]))
            [:dispatch [:qsm/delete-uploaded-cert-files qpe-identity (:id data) site-plan-key]])
          (when (and (some #{(:MQTTScheme data)} ["ssl" "wss"])
                     (seq files))
            [:dispatch [:qsm/save-uploaded-resource qpe-identity {:files         files
                                                                  :resource-type "zipFile"
                                                                  :target-id (:id data)}]])
          [:dispatch [:qpe/close-sidebar qpe-identity]]
          [:dispatch [:qsm/clear-siteresources qpe-identity]]]}))

(rf/reg-event-fx
  :qpe/control-universal-target
  (fn [_ [_ qpe-identity params]]
    (let [{:keys [mode site-plan-key MQTTScheme MQTTAddress WebsocketScheme WebsocketAddress]} params
          modified-scheme (if (= MQTTScheme "ssl/tls")
                            "ssl"
                            MQTTScheme)
          data (if (or MQTTScheme MQTTAddress)
                 (ut/decode-control-params (cond-> (set/rename-keys params {:name :target})
                                                   (or MQTTScheme MQTTAddress) (update :MQTTAddress
                                                                                       (if (some #{modified-scheme} ["ws" "wss"])
                                                                                         (fn [addr]
                                                                                           (pe-utils/update-address addr modified-scheme "/mqtt"))
                                                                                         (fn [addr]
                                                                                           (pe-utils/update-address addr modified-scheme ""))))))
                 (ut/decode-control-params (cond-> (set/rename-keys params {:name :target})
                                                   (or WebsocketScheme WebsocketAddress) (update :WebsocketAddress
                                                                                                 (fn [addr]
                                                                                                   (pe-utils/update-address addr WebsocketScheme "/"))))))]
      {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                            :event        [:qpe/control-universal-target (dissoc data :site-plan-key)]
                            :on-success   [:qpe/control-universal-target-success mode site-plan-key qpe-identity]
                            :on-error     [:qpe/sidebar-error qpe-identity]}]
            [:dispatch [:qpe/sidebar-pending qpe-identity true]]]})))

(rf/reg-event-db
  :qpe/sidebar-pending
  (fn [db [_ qpe-identity pending?]]
    (assoc-in db [:qpes qpe-identity :ui :sidebar :pending?] pending?)))

(rf/reg-event-db
  :qpe/sidebar-error
  (fn [db [_ qpe-identity error]]
    (-> db
        (assoc-in [:qpes qpe-identity :ui :sidebar :error] error)
        (assoc-in [:qpes qpe-identity :ui :sidebar :pending?] false))))

(rf/reg-event-fx
  :qpe/enable-location-based-commands
  (fn [_ [_ qpe-identity data]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/enable-location-based-commands data]}]]}))

(rf/reg-event-fx
  :qpe/req-logfile-list
  (fn [{db :db} [_ {:keys [site-id qpe-identity]}]]
    {:db (assoc-in db [:qpes (or qpe-identity (:qpe-identity db)) :logfile-list-loading] true)
     :fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :site-id site-id
                          :event        [:qpe/req-logfile-list]}]]}))

(rf/reg-event-fx
  :qpe/req-logfile-load
  (fn [{db :db} [_ qpe-identity file-name]]
    {:db (assoc-in db [:qpes qpe-identity :logfile-content] {:name    file-name
                                                             :state   :loading
                                                             :content nil})
     :fx [[:http-xhrio {:method          :get
                        :uri             (str "/qpe/" qpe-identity "/qsm/log-file/" file-name)
                        :on-success      [:qpe/req-logfile-load-on-success qpe-identity]
                        :on-failure      [:qpe/req-logfile-load-on-failure qpe-identity]
                        ;; The route can send log data in both plain or json format, request json response
                        ;; for this case.
                        ;; Current UI code wants keep string keys in the log events.
                        :response-format (ajax/json-response-format {:keywords? false})}]]}))

(rf/reg-event-db
  :qpe/req-logfile-load-on-success
  (fn [db [_ qpe-identity resp]]
    ;; Convert top level keys to keywords.
    (assoc-in db [:qpes qpe-identity :logfile-content] {:name (get resp "name")
                                                        :content (get resp "content")})))

(rf/reg-event-fx
  :qpe/req-logfile-delete
  (fn [{db :db} [_ qpe-identity file-name]]
    {:db (update-in db [:qpes qpe-identity] dissoc :logfile-content)
     :fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/req-logfile-delete file-name]}]]}))

(rf/reg-event-fx
  :qpe/file-delete-error
  (fn [_ [_ _qpe-identity error]]
    {:fx [[:dispatch [:notifications/notify
                      {:type       :error
                       :heading    "Action failed!"
                       :message    (:err-msg error)}]]]}))

(rf/reg-event-fx
  :qpe/req-output-data-files-metadata
  (fn [_ [_ {:keys [qpe-identity site-id]}]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :site-id      site-id
                          :event        [:qpe/req-output-data-files-metadata]}]]}))

(rf/reg-event-db
  :qpe/output-data-files-delete-success
  (fn [db [_ qpe-identity]]
    (-> db
        (update-in [:qpes qpe-identity :ui :pe-logs] dissoc :select-all?)
        (update-in [:qpes qpe-identity :ui :pe-logs] dissoc :output-data-logs))))

;; FIXME: This shouldn't be named req-
(rf/reg-event-fx
  :qpe/req-output-data-files-delete
  (fn [_ [_ qpe-identity file-names]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/req-output-data-files-delete file-names]
                          :on-success   [:qpe/output-data-files-delete-success qpe-identity]
                          :on-error [:notifications/notify
                                     {:type       :error
                                      :heading    "Failed to delete files!"
                                      :message    (str "Failed to delete selected files: " (str/join "," file-names))}]}]]}))

;; Handles the response from qpe with the event name `:qpe/output-data-files-metadata`
(rf/reg-event-db
  :qpe/output-data-files-metadata
  (fn [db [_ qpe-identity metadata]]
    (assoc-in db [:qpes qpe-identity :output-data-files-metadata] metadata)))

(rf/reg-event-db
  :qpe/remove-output-data-files-metadata
  (fn [db [_ qpe-identity _]]
    (update-in db [:qpes (or qpe-identity (:qpe-identity db))] dissoc :output-data-files-metadata)))

(rf/reg-event-fx
  :qpe/req-binarylog-files-metadata
  (fn [_ [_ {:keys [qpe-identity site-id]}]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :site-id site-id
                          :event        [:qpe/req-binarylog-files-metadata]}]]}))

;; FIXME: This shouldn't be named req-
(rf/reg-event-fx
  :qpe/req-binarylog-file-delete
  (fn [_ [_ qpe-identity file-name]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/req-binarylog-file-delete file-name]
                          :on-success   :ignore
                          :on-error     [:qpe/file-delete-error qpe-identity]}]]}))

;; Handles the response from qpe with the event name `:qpe/binarylog-files-metadata`
(rf/reg-event-db
  :qpe/binarylog-files-metadata
  (fn [db [_ qpe-identity metadata]]
    (assoc-in db [:qpes qpe-identity :binarylog-files-metadata] metadata)))

(rf/reg-event-db
  :qpe/remove-binarylog-files-metadata
  (fn [db [_ qpe-identity _]]
    (update-in db [:qpes (or qpe-identity (:qpe-identity db))] dissoc :binarylog-files-metadata)))

(rf/reg-event-fx
  :qpe/req-data-player-files-metadata
  (fn [_ [_ {:keys [qpe-identity site-id]}]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :site-id site-id
                          :event        [:qpe/req-data-player-files-metadata]}]]}))

;; FIXME: This shouldn't be named req-
(rf/reg-event-fx
  :qpe/req-data-player-file-delete
  (fn [_ [_ qpe-identity file-name]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/req-data-player-file-delete file-name]
                          :on-success   :ignore
                          :on-error     [:qpe/file-delete-error qpe-identity]}]]}))

;; Handles the response from qpe with the event name `:qpe/data-player-files-metadata`
(rf/reg-event-db
  :qpe/data-player-files-metadata
  (fn [db [_ qpe-identity metadata]]
    (assoc-in db [:qpes qpe-identity :data-player-files-metadata] metadata)))

(rf/reg-event-db
  :qpe/remove-data-player-files-metadata
  (fn [db [_ qpe-identity _]]
    (update-in db [:qpes (or qpe-identity (:qpe-identity db))] dissoc :data-player-files-metadata)))

(rf/reg-event-fx
  :qpe/req-current-log-subscribe
  (fn [_ [_ {:keys [qpe-identity site-id]}]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :site-id site-id
                          :event        [:qpe/req-subscribe-to-log true]}]]}))

(rf/reg-event-fx
  :qpe/req-current-log-unsubscribe
  (fn [_ [_ {:keys [qpe-identity site-id]}]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :site-id site-id
                          :event        [:qpe/req-subscribe-to-log false]}]]}))

(rf/reg-event-db
  :qpe/toggle-sidebar
  (fn [db [_ qpe-identity sidebar-value]]
    ;; If type and (optional) id are the same as the currently open sidebar,
    ;; close sidebar. Else open it.
    (if (= (select-keys sidebar-value [:type :id])
           (select-keys (get-in db [:qpes qpe-identity :ui :sidebar]) [:type :id]))
      (update-in db [:qpes qpe-identity :ui] dissoc :sidebar)
      (assoc-in db [:qpes qpe-identity :ui :sidebar] sidebar-value))))

(rf/reg-event-db
  :qpe/close-sidebar
  (fn [db [_ qpe-identity]]
    (update-in db [:qpes qpe-identity :ui] dissoc :sidebar)))

(rf/reg-event-fx
  :qpe/update-locator-fw
  (fn [_ [_ qpe-identity locator-ids]]
    {:fx [[:http-xhrio {:method          :post
                        :uri             (str "/qpe/" qpe-identity "/v2/upgradeAllFW")
                        :url-params      {:locator (str/join "," locator-ids)
                                          :maxnum 10}
                        :on-success      [:qpe/update-locator-fw-result qpe-identity]
                        :on-failure      [:qpe/update-locator-fw-result qpe-identity]
                        :format          (ajax/json-request-format)
                        :response-format (ajax/json-response-format {:keywords? true})}]
          [:dispatch [:qpe/fw-update-pending qpe-identity true (set locator-ids)]]]}))

(rf/reg-event-db
  :qpe/fw-update-pending
  (fn [db [_ qpe-identity pending? locator-ids]]
    (cond-> (assoc-in db [:qpes qpe-identity :ui :fw-update-pending?] pending?)
            (and pending? locator-ids) (assoc-in [:qpes qpe-identity :ui :fw-update-pending-locator-ids] locator-ids)
            (not pending?)              (update-in [:qpes qpe-identity :ui] dissoc :fw-update-pending-locator-ids))))

(rf/reg-event-fx
  :qpe/update-locator-fw-result
  (fn [_ [_ qpe-identity {:keys [status message] :as _result}]]
    (when-not (and (= status :Ok) (= message "FW upgrade started"))
      {:fx [[:dispatch [:qpe/fw-update-pending qpe-identity false]]]})))

(rf/reg-event-fx
  :qpe-dispatch
  (fn [_ [_ qpe-identity qpe-event ?opts]]
    {:fx [[:qpe-dispatch (merge ?opts                       ; could be on-success etc, see connection.cljs
                                {:qpe-identity qpe-identity
                                 :event        qpe-event})]]}))

(rf/reg-event-db
  :qpe/position-events
  (fn [db [_ {:keys [qpe-identity stats]}]]
    (assoc-in db [:qpes qpe-identity :position-events] stats)))

;; Only available on QSM, will return nil on QPE-next.
(rf/reg-sub :qsm/qpe-configuration-value
  (fn [[_ qpe-identity _k]]
    (rf/subscribe [:data :quuppa.peweb.query/state :authorized-qpes/all :result qpe-identity :configuration]))
  (fn [configuration [_ _ k]]
    (get configuration k)))

(rf/reg-event-fx
  :qpe/req-list-pe-logs
  (fn [_ [_ {:keys [qpe-identity site-id]} log-type]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :site-id site-id
                          :event [:qpe/req-list-pe-logs {:log-type log-type}]}]
          [:dispatch [:qpe/clear-selected-logs {:qpe-identity qpe-identity
                                                :log-type log-type}]]]}))

(rf/reg-event-fx
  :qpe/req-delete-pe-logs
  (fn [_ [_ qpe-identity {:keys [log-type file-names] :as args}]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event [:qpe/req-delete-pe-logs args]
                          :on-success [:qpe/req-list-pe-logs {:qpe-identity qpe-identity} log-type]
                          :on-error [:notifications/notify
                                     {:type       :error
                                      :heading    "Failed to delete files!"
                                      :message    (str "Failed to delete selected files: " (str/join "," file-names))}]}]]}))

(rf/reg-event-db
  :qpe/clear-selected-logs
  (fn [db [_ {:keys [qpe-identity log-type]}]]
    (-> db
        (update-in [:qpes qpe-identity :ui :pe-logs log-type] dissoc :selected-files))))

(rf/reg-event-db
  :qpe/select-log-file
  (fn [db [_ qpe-identity log-type file-name]]
    (update-in db
               [:qpes (or qpe-identity (:qpe-identity db)) :ui :pe-logs log-type :selected-files]
               (fn [selected-files file-name]
                 ;; Ensure we start with an empty set when the value is first set
                 (let [f (fnil (if (contains? selected-files file-name)
                                 disj
                                 conj) #{})]
                   (f selected-files file-name)))
               file-name)))

(rf/reg-event-db
  :qpe/select-all-output-data-files
  (fn [db [_ qpe-identity search-string]]
    (let [current-select-all? (get-in db [:qpes qpe-identity :ui :pe-logs :select-all?])
          new-select-all? (not current-select-all?)
          all-files (get-in db [:qpes qpe-identity :output-data-files-metadata])
          filtered-files (pe-utils/filter-output-data-files search-string all-files)
          filtered-filenames (set (map :filename filtered-files))]
      (if new-select-all?
        (-> db
            (assoc-in [:qpes qpe-identity :ui :pe-logs :select-all?] true)
            (assoc-in [:qpes qpe-identity :ui :pe-logs :output-data-logs :selected-files] filtered-filenames))
        (-> db
            (update-in [:qpes qpe-identity :ui :pe-logs] dissoc :select-all?)
            (update-in [:qpes qpe-identity :ui :pe-logs :output-data-logs] dissoc :selected-files))))))

(rf/reg-event-db
  :qpe/set-output-data-files-search
  (fn [db [_ qpe-identity value]]
    (assoc-in db [:qpes qpe-identity :ui :pe-logs :output-data-logs :search] value)))

(rf/reg-event-db
  :qpe/pe-logs
  (fn [db [_ qpe-identity {:keys [log-type logs]}]]
    (assoc-in db [:qpes qpe-identity :pe-logs log-type] logs)))

(rf/reg-event-db
  :qpe/remove-pe-logs
  (fn [db [_ qpe-identity log-type]]
    (update-in db [:qpes (or qpe-identity (:qpe-identity db)) :pe-logs] dissoc log-type)))

;;
;; Locator and tag table sidebar
;;

(rf/reg-event-fx
  ::open-panel
  (fn [{:keys [db]} [_ _qpe-identity k]]
    (let [current-pref (get-in db [:user-preferences k])]
      {:fx [[:update-query [(fn [_] {:panel k})]]]
       :db (assoc-in db [:user-preferences :previous-value] {k current-pref})})))


(rf/reg-event-fx
  ::apply-panel-changes
  (fn [{:keys [db]} [_ k]]
    (if (nil? k)
      {:db db}
      (let [value (get-in db [:user-preferences k])]
        {:db (update db :user-preferences dissoc :previous-value)
         :fx [[:update-query [(fn [q] (dissoc q :panel))]]
              [:update-local-storage {:local-storage-key "user-preferences"
                                      :value {k value}}]]}))))

(rf/reg-event-db
  ::clean-select-all
  (fn [db [_ _]]
    (update db :user-preferences dissoc :locator-columns-select-all? :tag-columns-select-all?)))

(rf/reg-event-fx
  ::cancel-panel-changes
  (fn [{:keys [db]} [_ qpe-identity k]]
    (let [prev (get-in db [:user-preferences :previous-value k])]
      {:db (-> db
               (assoc-in [:user-preferences k] prev)
               (update :user-preferences dissoc :previous-value))
       :fx [[:update-query [(fn [q] (dissoc q :panel))]]
            [:dispatch [::clean-select-all]]]})))


(rf/reg-event-db :qpe/universal-target-form-upload-file
                 (fn [db [_ qpe-identity value]]
                   (update-in db [:qpes (or qpe-identity (:qpe-identity db)) :ui :sidebar :ut :files] conj value)))

(rf/reg-event-fx
  :qpe/upload-file
  (fn [_ [_ qpe-identity form-data]]
    {:fx [[:http-xhrio {:method          :post
                        :uri             "/siteresources"
                        :body            form-data
                        :on-success      [:qpe/universal-target-form-upload-file qpe-identity]
                        :on-failure      [:notifications/notify {:type       :error
                                                                 :heading    "Upload failed"
                                                                 :message    "Upload failed"}]
                        :response-format (ajax/json-response-format {:keywords? true})}]]}))

(rf/reg-event-fx
  :qsm/save-uploaded-resource
  (fn [_ [_ qpe-identity data]]
    {:fx [[:server-dispatch {:event   [:qsm/save-uploaded-resource (merge {:qpe-identity qpe-identity}
                                                                          data)]
                             :timeout 3000
                             :on-success   [:qsm/download-uploaded-resource data qpe-identity]
                             :on-error     [:qpe/sidebar-error qpe-identity]}]]}))

(rf/reg-event-db
  :qsm/save-site-resource-id
  (fn [db [_ qpe-identity response]]
    (-> db
        (assoc-in [:qpes qpe-identity :siteresource] (str (-> response :body))))))

(rf/reg-event-fx
  :qsm/delete-uploaded-cert-files
  (fn [_ [_ qpe-identity target-id site-plan-key]]
    {:fx [[:server-dispatch {:event   [:qsm/delete-uploaded-cert-files target-id]
                             :timeout 3000
                             :on-success   [:qsm/delete-uploaded-cert-files-success qpe-identity {:site-plan-key site-plan-key
                                                                                                  :target-id target-id}]
                             :on-error     [:notifications/notify]}]]}))

(rf/reg-event-fx
  :qsm/delete-uploaded-cert-files-success
  (fn [_ [_ qpe-identity data]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/delete-uploaded-cert-files data]}]
          [:dispatch [:qsm/clear-siteresources qpe-identity]]]}))

(rf/reg-event-fx
  :qsm/get-siteresource-by-target-id
  (fn [_ [_ qpe-identity data]]
    {:fx [[:server-dispatch {:event   [:qsm/get-siteresource-by-target-id data]
                             :timeout 3000
                             :on-success   [:qsm/save-site-resource-id qpe-identity]}]]}))

(rf/reg-event-db
  :qsm/download-uploaded-resource-failed
  (fn [db [_ qpe-identity result]]
    (-> db
        (assoc-in [:qpes qpe-identity :download-uploaded-resource :xhrio-error] result))))

(rf/reg-event-fx
  :qsm/download-uploaded-resource-success
  (fn [_ [_ {:keys [qpe-identity files-metadata target-id]} data]]
    {:fx [[:qpe-dispatch {:qpe-identity qpe-identity
                          :event        [:qpe/persist-uploaded-cert-files (merge data {:target-id target-id})]}]
          [:dispatch [:qsm/delete-local-files files-metadata]]]}))

(rf/reg-event-fx
  :qsm/download-uploaded-resource
  (fn [_ [_ data qpe-identity res]]
    (let [site-resource-id (->> res :body :id)
          files-metadata (->> res :body :files-metadata)]
      {:fx [[:http-xhrio {:method          :get
                          :uri             (str "/siteresources/" site-resource-id)
                          :on-success      [:qsm/download-uploaded-resource-success {:qpe-identity qpe-identity
                                                                                     :files-metadata files-metadata
                                                                                     :target-id (:target-id data)}]
                          :on-failure      [:qsm/download-uploaded-resource-failed qpe-identity]
                          :response-format (ajax/json-response-format {:keywords? true})}]]})))

(rf/reg-event-fx
  :qsm/delete-local-files
  (fn [_ [_ data]]
    {:fx [[:server-dispatch {:event   [:qsm/delete-local-files data]
                             :on-success :ignore
                             :on-error [:notifications/notify]}]]}))

(rf/reg-event-db
  :qsm/clear-siteresources
  (fn [db [_ qpe-identity]]
    (-> db
        (update-in [:qpes qpe-identity] dissoc :siteresource))))
